/** The Overview page's sections: next step, referral link, balance, activity. */
import { Chip } from "@nt/ui/chip";
import { cn } from "@nt/ui/cn";
import { buttonVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import { Tooltip, TooltipContent, TooltipTrigger } from "@nt/ui/tooltip";
import {
  ArrowRight,
  Banknote,
  CircleAlert,
  CircleDollarSign,
  Undo2,
  UserPlus,
  Wallet,
} from "lucide-react";
import type { ComponentType } from "react";
import { Link } from "react-router";

import { CopyField } from "@/components/copy-field";
import { type QueryLike, QueryView } from "@/components/query-view";
import { Section } from "@/components/section";
import { money, OWED_TO_US_NOTE, shortDate } from "@/lib/format";
import { setupStepsLeft } from "@/lib/queries";
import type { ActivityItem, LinksData, OverviewData } from "@/lib/types";

/** The one thing to do next, while there is one (not once it is "track referrals"). */
export function NextStepBanner({ step }: { step: OverviewData["nextStep"] }) {
  if (step.key === "track_referrals") return null;
  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          <CircleAlert aria-hidden className="size-4 shrink-0 text-primary" />
          {step.title}
        </span>
      }
      description={step.body}
      actions={
        <Link to={step.to} className={buttonVariants()}>
          {step.actionLabel}
          <ArrowRight aria-hidden />
        </Link>
      }
    />
  );
}

const READ_ONLY_REASON = "Your account is read-only";

/** The way to the create-link drawer; a read-only partner, who has no drawer, sees it disabled with the reason. */
function CreateLinkAction({ readOnly }: { readOnly: boolean }) {
  const look = buttonVariants({ variant: "secondary", size: "sm" });
  if (!readOnly) {
    return (
      <Link to="/links?create=1" className={look}>
        Create a custom link
      </Link>
    );
  }
  // A disabled button gets no pointer or focus events, so the span carries the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span tabIndex={0} className="inline-flex rounded-full" />}
      >
        <button type="button" disabled className={look}>
          Create a custom link
          <span className="sr-only">. {READ_ONLY_REASON}.</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{READ_ONLY_REASON}</TooltipContent>
    </Tooltip>
  );
}

/** The main short link to copy, the code, and the way to a custom link. */
export function ReferralLinkSection({
  links,
  readOnly = false,
}: {
  links: QueryLike<LinksData>;
  readOnly?: boolean;
}) {
  const primary = links.data?.primary ?? null;
  return (
    <Section
      title="Your referral link"
      description="Share it anywhere. Everyone who signs up through it counts as your referral."
      actions={primary ? <CreateLinkAction readOnly={readOnly} /> : null}
    >
      <QueryView query={links} what="your referral link" rows={1}>
        {(data) =>
          data.primary ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <CopyField
                label="Short link"
                value={data.primary.shortUrl}
                className="flex-1"
              />
              <div className="flex flex-col gap-1.5">
                <span className="text-sm text-muted-foreground">Your code</span>
                <div className="flex h-control items-center">
                  <Chip tone="surface" size="lg" className="font-mono">
                    {data.primary.code}
                  </Chip>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              You do not have a referral code yet. Write to
              support@nonresident.tax and we will set one up for you.
            </p>
          )
        }
      </QueryView>
    </Section>
  );
}

function BalanceRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="min-w-0">
        <span className="text-sm text-foreground">{label}</span>
        {hint ? (
          <span className="block text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </dt>
      <dd className="shrink-0 text-sm font-medium text-foreground tabular-nums">
        {value}
      </dd>
    </div>
  );
}

/** A clawback took the payable balance below zero: nothing moves toward the minimum yet. */
function OwedToUs({ cents }: { cents: number }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm text-muted-foreground">Owed to us</span>
        <span className="text-sm font-medium text-foreground tabular-nums">
          {money(Math.abs(cents))}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{OWED_TO_US_NOTE}</p>
    </div>
  );
}

function TowardMinimum({ data }: { data: OverviewData }) {
  const payable = data.balance.payableCents;
  const min = data.minPayoutCents;
  const pct = min > 0 ? Math.min(100, Math.round((payable / min) * 100)) : 100;
  const short = Math.max(0, min - payable);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          Payable toward the minimum
        </span>
        <span className="text-sm font-medium text-foreground tabular-nums">
          {money(payable)} of {money(min)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Payable balance toward the minimum payout"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="h-2 overflow-hidden rounded-pill bg-muted"
      >
        <div
          className="h-full rounded-pill bg-primary transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {short > 0 ? (
          `${money(short)} more to reach the ${money(min)} minimum.`
        ) : setupStepsLeft(data) > 0 ? (
          <>
            You reached the minimum.{" "}
            <Link to="/payouts" className="underline underline-offset-2">
              Finish the steps in Payouts
            </Link>{" "}
            so it can go out.
          </>
        ) : (
          `You reached the minimum. It goes out on ${shortDate(data.nextPayoutDate)}.`
        )}
      </p>
    </div>
  );
}

/** What the partner is owed, and how far the payable balance is from the minimum. */
export function BalancePanel({ data }: { data: OverviewData }) {
  const b = data.balance;
  const min = data.minPayoutCents;
  return (
    <Section
      title="Balance"
      description={`Payouts go out monthly once your payable balance reaches ${money(min)}.`}
    >
      <div className="flex flex-col gap-4">
        {b.payableCents < 0 ? (
          <OwedToUs cents={b.payableCents} />
        ) : (
          <TowardMinimum data={data} />
        )}
        <dl className="divide-y divide-border">
          <BalanceRow
            label="Pending"
            value={money(b.pendingCents)}
            hint={`Paid by the customer, in the ${data.holdDays}-day hold`}
          />
          <BalanceRow
            label="On hold"
            value={money(b.onHoldCents)}
            hint="Under review, nothing needed from you"
          />
          <BalanceRow
            label="Payable"
            value={money(Math.max(0, b.payableCents))}
            hint="Ready for the next payout"
          />
          <BalanceRow label="Paid to date" value={money(b.paidCents)} />
          <BalanceRow
            label="Next payout"
            value={shortDate(data.nextPayoutDate)}
          />
          <BalanceRow label="Minimum payout" value={money(min)} />
        </dl>
      </div>
    </Section>
  );
}

const KIND_ICON: Record<
  ActivityItem["kind"],
  ComponentType<{ className?: string; "aria-hidden"?: boolean }>
> = {
  signup: UserPlus,
  fee: CircleDollarSign,
  payable: Wallet,
  paid: Banknote,
  payout: Banknote,
  reversed: Undo2,
};

function ActivityRowBody({ item }: { item: ActivityItem }) {
  const Icon = KIND_ICON[item.kind] ?? CircleDollarSign;
  return (
    <>
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-pill bg-muted text-muted-foreground"
      >
        <Icon aria-hidden className="size-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm text-foreground">{item.text}</span>
        <span className="text-xs text-muted-foreground">
          {shortDate(item.at)}
        </span>
      </span>
      {item.amountCents !== null ? (
        <span
          className={cn(
            "shrink-0 text-sm font-medium tabular-nums",
            item.kind === "reversed" ? "text-danger" : "text-foreground",
          )}
        >
          {money(item.amountCents)}
        </span>
      ) : null}
    </>
  );
}

/** The latest sign-ups, fees and payouts; a customer's row opens that customer. */
export function ActivityPanel({ items }: { items: ActivityItem[] }) {
  return (
    <Section
      title="Recent activity"
      description="Your latest sign-ups, fees and payouts."
    >
      {items.length === 0 ? (
        <EmptyState
          title="Nothing yet"
          description="Share your link to see sign-ups here."
          className="py-6"
        />
      ) : (
        <ul className="-mx-2 flex flex-col">
          {items.map((item, i) => (
            <li key={`${item.at}-${i}`}>
              {item.customerId ? (
                <Link
                  to={`/customers/${item.customerId}`}
                  className="flex items-center gap-3 rounded-control px-2 py-2.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <ActivityRowBody item={item} />
                </Link>
              ) : (
                <div className="flex items-center gap-3 px-2 py-2.5">
                  <ActivityRowBody item={item} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
